import { supabase, isSupabaseConfigured } from './supabase.js';

/**
 * SenritsuX Artist Web Service
 * Helper methods for data normalization, social URL formatting, directory utilities, and SEO slug handling.
 */

/**
 * Deterministically create a URL-friendly slug from an artist stage name or name
 */
export function slugifyArtist(stageNameOrName, fallbackId = '') {
  if (!stageNameOrName && !fallbackId) return 'artist';
  const raw = String(stageNameOrName || '').trim();
  const slug = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || (fallbackId ? String(fallbackId).slice(0, 8) : 'artist');
}

/**
 * Sanitize an external URL ensuring strictly http/https protocol and blocking malicious schemes.
 */
export function sanitizeExternalUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  // Disallow javascript:, data:, vbscript:, file: etc.
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('file:')
  ) {
    return null;
  }

  try {
    const parsed = new URL(trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Normalize an Instagram handle, profile URL, or raw string into a clean lowercase username.
 * Accepts:
 *   - "yuvaofficial"
 *   - "@yuvaofficial"
 *   - "https://instagram.com/yuvaofficial"
 *   - "https://www.instagram.com/yuvaofficial/"
 *   - "http://instagram.com/yuvaofficial?igsh=123"
 * Strips protocol, domain, @, query parameters, trailing slashes, and whitespace.
 */
export function normalizeInstagramUsername(input) {
  if (!input || typeof input !== 'string') return '';
  let str = input.trim();
  if (!str) return '';

  // Strip protocol
  str = str.replace(/^https?:\/\//i, '');
  // Strip www.
  str = str.replace(/^www\./i, '');
  // Strip domain
  str = str.replace(/^(instagram\.com|instagr\.am)\//i, '');
  // Strip query parameters and hashes
  str = str.split('?')[0].split('#')[0];
  // Strip leading @, slashes
  str = str.replace(/^[@/]+/, '');
  // Strip trailing slashes
  str = str.replace(/\/+$/, '');
  // Return trimmed lowercase username
  return str.trim().toLowerCase();
}

/**
 * Check if a normalized Instagram username is already claimed by another artist in Supabase.
 */
export async function checkInstagramUsernameAvailable(supabaseClient, rawUsername, currentUserId = null) {
  const normalized = normalizeInstagramUsername(rawUsername);
  if (!normalized) return { available: true, username: '' };
  if (!supabaseClient) return { available: true, username: normalized };

  try {
    // 1. Check artist_profiles
    const { data: artists } = await supabaseClient
      .from('artist_profiles')
      .select('id, user_id, instagram');

    if (Array.isArray(artists)) {
      for (const a of artists) {
        if (currentUserId && (a.user_id === currentUserId || a.id === currentUserId)) {
          continue;
        }
        if (normalizeInstagramUsername(a.instagram) === normalized) {
          return { available: false, username: normalized, conflictUserId: a.user_id };
        }
      }
    }

    // 2. Check profiles
    const { data: profiles } = await supabaseClient
      .from('profiles')
      .select('id, instagram');

    if (Array.isArray(profiles)) {
      for (const p of profiles) {
        if (currentUserId && p.id === currentUserId) {
          continue;
        }
        if (normalizeInstagramUsername(p.instagram) === normalized) {
          return { available: false, username: normalized, conflictUserId: p.id };
        }
      }
    }

    return { available: true, username: normalized };
  } catch (err) {
    console.warn('[artistWebService] Warning checking username availability:', err);
    return { available: true, username: normalized };
  }
}

/**
 * Safely format an Instagram handle or URL into a valid Instagram profile link
 */
export function formatInstagramUrl(handleOrUrl) {
  if (!handleOrUrl) return null;
  let str = String(handleOrUrl).trim();
  if (!str || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined') return null;

  // If already starts with http:// or https://
  if (/^https?:\/\//i.test(str)) {
    return sanitizeExternalUrl(str);
  }

  // If starts with instagram.com or www.instagram.com without protocol
  if (/^(www\.)?instagram\.com\//i.test(str)) {
    return sanitizeExternalUrl(`https://${str}`);
  }

  // Otherwise treat as handle/username
  const cleanHandle = str.replace(/^@+/, '').replace(/^\/+|\/+$/g, '').trim();
  return cleanHandle ? sanitizeExternalUrl(`https://www.instagram.com/${cleanHandle}`) : null;
}

/**
 * Safely format a YouTube handle, channel ID, or URL into a valid YouTube link
 */
export function formatYoutubeUrl(handleOrUrl) {
  if (!handleOrUrl) return null;
  let str = String(handleOrUrl).trim();
  if (!str || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined') return null;

  // If already starts with http:// or https://
  if (/^https?:\/\//i.test(str)) {
    return sanitizeExternalUrl(str);
  }

  // If starts with youtube.com, www.youtube.com, or youtu.be without protocol
  if (/^(www\.)?(youtube\.com|youtu\.be)\//i.test(str)) {
    return sanitizeExternalUrl(`https://${str}`);
  }

  if (str.startsWith('@')) {
    return sanitizeExternalUrl(`https://www.youtube.com/${str}`);
  }
  return sanitizeExternalUrl(`https://www.youtube.com/@${str}`);
}

/**
 * Safely format a Spotify artist link, URI, or ID into a valid Spotify URL
 */
export function formatSpotifyUrl(urlOrId) {
  if (!urlOrId) return null;
  let str = String(urlOrId).trim();
  if (!str || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined') return null;

  // If already starts with http:// or https://
  if (/^https?:\/\//i.test(str)) {
    return sanitizeExternalUrl(str);
  }

  // If starts with open.spotify.com or spotify.com without protocol
  if (/^(open\.)?spotify\.com\//i.test(str)) {
    return sanitizeExternalUrl(`https://${str.replace(/^spotify\.com/i, 'open.spotify.com')}`);
  }

  // If spotify URI: spotify:artist:<id>
  if (/^spotify:artist:([a-zA-Z0-9]+)/i.test(str)) {
    const artistId = str.split(':')[2];
    return sanitizeExternalUrl(`https://open.spotify.com/artist/${artistId}`);
  }

  // Otherwise treat as raw artist ID
  const cleanId = str.replace(/^\/+|\/+$/g, '').trim();
  return cleanId ? sanitizeExternalUrl(`https://open.spotify.com/artist/${cleanId}`) : null;
}

/**
 * Safely format an Apple Music link or ID into a valid Apple Music URL
 */
export function formatAppleMusicUrl(urlOrId) {
  if (!urlOrId) return null;
  const trimmed = String(urlOrId).trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return sanitizeExternalUrl(trimmed);
  }
  return sanitizeExternalUrl(`https://music.apple.com/artist/${trimmed}`);
}

/**
 * Safely format a YouTube Music link or handle into a valid YouTube Music URL
 */
export function formatYoutubeMusicUrl(urlOrId) {
  if (!urlOrId) return null;
  const trimmed = String(urlOrId).trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return sanitizeExternalUrl(trimmed);
  }
  if (trimmed.startsWith('channel/') || trimmed.startsWith('UC')) {
    return sanitizeExternalUrl(`https://music.youtube.com/${trimmed}`);
  }
  return sanitizeExternalUrl(`https://music.youtube.com/search?q=${encodeURIComponent(trimmed)}`);
}

/**
 * Normalize genre to uppercase trimmed string for consistent comparison and display.
 * Avoids duplicate filters (e.g. 'Hip Hop', 'hip hop', 'HIP HOP' all become 'HIP HOP').
 */
export function normalizeGenre(genre) {
  if (!genre || typeof genre !== 'string') return '';
  return genre.trim().toUpperCase();
}

/**
 * Merge artist_profiles and profiles data, using profiles as source of truth for display fields
 */
export function normalizeArtistData(artistProfile, profile) {
  const prof = profile || {};
  const stageName = prof.stage_name || artistProfile?.stage_name || 'Artist';
  const name = prof.name || artistProfile?.name || '';
  const rawGenre = prof.genre || artistProfile?.genre || '';
  const genre = rawGenre.trim();
  const normalizedGenre = normalizeGenre(rawGenre);

  // Secondary Genre support
  const rawSecondaryGenre = prof.secondary_genre || artistProfile?.secondary_genre || '';
  const secondaryGenre = rawSecondaryGenre.trim() || null;
  const normalizedSecondaryGenre = normalizeGenre(rawSecondaryGenre);

  const city = prof.city || artistProfile?.city || '';
  const avatarUrl = prof.avatar_url || artistProfile?.avatar_url || null;
  const spotifyLink = prof.spotify_link || artistProfile?.spotify_link || null;
  const appleMusicLink = prof.apple_music_link || artistProfile?.apple_music_link || null;
  const youtubeMusicLink = prof.youtube_music_link || artistProfile?.youtube_music_link || null;
  const instagram = prof.instagram || artistProfile?.instagram || null;
  const youtube = prof.youtube || artistProfile?.youtube || null;
  const bio = artistProfile?.bio || prof.bio || '';
  
  // 150-character public Artist Web about description
  const rawArtistWebAbout = artistProfile?.artist_web_about || prof.artist_web_about || '';
  const artistWebAbout = rawArtistWebAbout.trim().slice(0, 150);

  const userId = artistProfile?.user_id || prof.id;
  const id = artistProfile?.id || prof.id || userId;
  const starCount = typeof prof.star_count === 'number' ? prof.star_count : 0;

  const rawInstagram = prof.instagram || artistProfile?.instagram || null;
  const instagramUsername = normalizeInstagramUsername(rawInstagram);

  return {
    id,
    user_id: userId,
    stage_name: stageName,
    name,
    genre,
    normalized_genre: normalizedGenre,
    raw_genre: rawGenre,
    secondary_genre: secondaryGenre || null,
    normalized_secondary_genre: normalizedSecondaryGenre || '',
    raw_secondary_genre: rawSecondaryGenre || '',
    city,
    avatar_url: avatarUrl,
    spotify_link: formatSpotifyUrl(spotifyLink),
    apple_music_link: formatAppleMusicUrl(appleMusicLink),
    youtube_music_link: formatYoutubeMusicUrl(youtubeMusicLink),
    instagram: formatInstagramUrl(rawInstagram),
    raw_instagram: rawInstagram,
    instagram_username: instagramUsername,
    canonical_artist_url: instagramUsername
      ? `https://senritsux.in/artist/${instagramUsername}`
      : `https://senritsux.in/artist/${slugifyArtist(stageName || name, userId)}`,
    canonical_path: instagramUsername
      ? `/artist/${instagramUsername}`
      : `/artist/${slugifyArtist(stageName || name, userId)}`,
    youtube: formatYoutubeUrl(youtube),
    bio,
    artist_web_about: artistWebAbout,
    star_count: Math.max(0, Math.min(5, starCount)),
    is_visible: artistProfile?.is_visible !== false,
    created_at: artistProfile?.created_at || prof.created_at,
    slug: slugifyArtist(stageName || name, userId),
  };
}

/**
 * Fetch a single visible artist by their Instagram username, slug, user ID, or record ID.
 *
 * Strategy:
 *   1. Fast-path: direct Supabase query matching normalized Instagram username
 *   2. Full-scan fallback: fetch all visible profiles and match in JS
 *
 * Error handling:
 *   - Supabase errors are logged but do NOT abort the lookup silently.
 *   - If the fast-path fails, the full-scan is still attempted.
 *   - Returns null only when genuinely no matching visible profile is found.
 */
export async function fetchArtistBySlugOrId(slug) {
  if (!slug || !isSupabaseConfigured()) return null;

  const cleanIdent = String(slug || '').trim();
  if (!cleanIdent) return null;

  const normalizedIgTarget = normalizeInstagramUsername(cleanIdent);
  const cleanLower = cleanIdent.toLowerCase();

  try {
    // ── FAST PATH: direct lookup by normalized Instagram username ──────────────
    // Try to find the artist directly using a case-insensitive LIKE match on the
    // raw instagram column. This avoids loading the entire artist roster for
    // individual profile page loads, and is more resilient to Supabase errors.
    if (normalizedIgTarget) {
      let fastArtistRow = null;
      let fastProfileRow = null;

      try {
        // Match: stored value could be "sjsamjha", "@sjsamjha",
        // "https://instagram.com/sjsamjha", or "https://www.instagram.com/sjsamjha/"
        const { data: fastRows, error: fastErr } = await supabase
          .from('artist_profiles')
          .select('*')
          .eq('is_visible', true)
          .or(
            `instagram.ilike.${normalizedIgTarget},` +
            `instagram.ilike.@${normalizedIgTarget},` +
            `instagram.ilike.%instagram.com/${normalizedIgTarget}%,` +
            `instagram.ilike.%instagram.com/${normalizedIgTarget}/%`
          );

        if (!fastErr && Array.isArray(fastRows) && fastRows.length > 0) {
          // Verify with JS normalizer to avoid false positives
          fastArtistRow = fastRows.find((r) =>
            normalizeInstagramUsername(r.instagram) === normalizedIgTarget
          ) || null;
        }
      } catch (fastLookupErr) {
        console.warn('[artistWebService] Fast-path artist_profiles lookup failed:', fastLookupErr);
      }

      // If artist_profiles did not match or had null instagram, check profiles table directly
      if (!fastArtistRow) {
        try {
          const { data: profMatches, error: profMatchErr } = await supabase
            .from('profiles')
            .select('id, name, stage_name, genre, secondary_genre, city, avatar_url, spotify_link, apple_music_link, youtube_music_link, instagram, youtube, star_count, artist_web_about, created_at')
            .or(
              `instagram.ilike.${normalizedIgTarget},` +
              `instagram.ilike.@${normalizedIgTarget},` +
              `instagram.ilike.%instagram.com/${normalizedIgTarget}%,` +
              `instagram.ilike.%instagram.com/${normalizedIgTarget}/%`
            );

          if (!profMatchErr && Array.isArray(profMatches) && profMatches.length > 0) {
            const matchedProf = profMatches.find((p) =>
              normalizeInstagramUsername(p.instagram) === normalizedIgTarget
            );

            if (matchedProf) {
              // Check if an artist_profiles record exists for this user
              const { data: artistRec } = await supabase
                .from('artist_profiles')
                .select('*')
                .eq('user_id', matchedProf.id)
                .maybeSingle();

              // If visible is explicitly false, do not show
              if (!artistRec || artistRec.is_visible !== false) {
                fastProfileRow = matchedProf;
                fastArtistRow = artistRec || {
                  id: matchedProf.id,
                  user_id: matchedProf.id,
                  stage_name: matchedProf.stage_name,
                  name: matchedProf.name,
                  is_visible: true,
                  instagram: matchedProf.instagram,
                };
              }
            }
          }
        } catch (profLookupErr) {
          console.warn('[artistWebService] Fallback profiles lookup by instagram failed:', profLookupErr);
        }
      }

      if (fastArtistRow) {
        if (!fastProfileRow && fastArtistRow.user_id) {
          try {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('id, name, stage_name, genre, secondary_genre, city, avatar_url, spotify_link, apple_music_link, youtube_music_link, instagram, youtube, star_count, artist_web_about, created_at')
              .eq('id', fastArtistRow.user_id)
              .maybeSingle();
            fastProfileRow = profileData || {};
          } catch {
            fastProfileRow = {};
          }
        }

        // Resolve star count for this artist
        let fastStar = 0;
        try {
          const { data: auditFast } = await supabase
            .from('admin_audit_logs')
            .select('target_id, new_value')
            .eq('action', 'artist_star_assignment')
            .eq('target_id', fastArtistRow.user_id);
          if (auditFast && auditFast.length > 0) {
            const latest = auditFast[auditFast.length - 1];
            fastStar = Number(latest.new_value?.star_count ?? 0);
          } else if (fastArtistRow.user_id === '0ac6ed34-39a9-476b-ae35-0b76ea402930') {
            fastStar = 5;
          } else if (typeof fastProfileRow?.star_count === 'number') {
            fastStar = fastProfileRow.star_count;
          }
        } catch {}

        const fastBase = normalizeArtistData(fastArtistRow, fastProfileRow || {});
        return { ...fastBase, star_count: Math.max(0, Math.min(5, fastStar)) };
      }
    }

    // ── FULL SCAN FALLBACK ───────────────────────────────────────────────────
    // Used when: fast path produced no result, OR the slug is a legacy slug/ID.

    // 1. Fetch audit star assignments
    let starMap = {
      '0ac6ed34-39a9-476b-ae35-0b76ea402930': 5,
    };
    try {
      const { data: auditStars } = await supabase
        .from('admin_audit_logs')
        .select('target_id, new_value, created_at')
        .eq('action', 'artist_star_assignment');
      if (auditStars) {
        auditStars.forEach((row) => {
          if (row.target_id && row.new_value?.star_count !== undefined) {
            starMap[row.target_id] = Number(row.new_value.star_count);
          }
        });
      }
    } catch {}

    // 2. Fetch all visible artist profiles
    const { data: rawArtists, error: artistError } = await supabase
      .from('artist_profiles')
      .select('*')
      .eq('is_visible', true);

    if (artistError) {
      console.error('[artistWebService] Error fetching artist_profiles:', artistError);
      // Do NOT return null here ,  continue with whatever data we have
    }

    if (!Array.isArray(rawArtists) || rawArtists.length === 0) {
      console.warn('[artistWebService] No visible artist profiles found. Check is_visible column values and RLS policies.');
      return null;
    }

    const userIds = rawArtists.map((a) => a.user_id).filter(Boolean);

    let rawProfiles = [];
    // Primary query with valid profiles fields (Note: profiles does not have bio column, it has artist_web_about)
    const resProf = await supabase
      .from('profiles')
      .select('id, name, stage_name, genre, secondary_genre, city, avatar_url, spotify_link, apple_music_link, youtube_music_link, instagram, youtube, star_count, artist_web_about, created_at')
      .in('id', userIds);

    if (resProf.error) {
      // Fallback 1: without extended DSP columns or secondary_genre if not yet migrated
      const resFallback1 = await supabase
        .from('profiles')
        .select('id, name, stage_name, genre, city, avatar_url, spotify_link, instagram, youtube, star_count, artist_web_about, created_at')
        .in('id', userIds);

      if (resFallback1.error && resFallback1.error.code === '42703') {
        // Fallback 2: without star_count
        const resFallback2 = await supabase
          .from('profiles')
          .select('id, name, stage_name, genre, city, avatar_url, spotify_link, instagram, youtube, created_at')
          .in('id', userIds);
        rawProfiles = resFallback2.data || [];
      } else {
        rawProfiles = resFallback1.data || [];
      }
    } else {
      rawProfiles = resProf.data || [];
    }

    const profileMap = (rawProfiles || []).reduce((acc, p) => {
      acc[p.id] = p;
      return acc;
    }, {});

    const normalizedArtists = rawArtists.map((a) => {
      const prof = profileMap[a.user_id] || {};
      const base = normalizeArtistData(a, prof);
      const resolvedStar =
        typeof prof.star_count === 'number'
          ? prof.star_count
          : typeof a.star_count === 'number'
          ? a.star_count
          : starMap[a.user_id] !== undefined
          ? starMap[a.user_id]
          : a.user_id === '0ac6ed34-39a9-476b-ae35-0b76ea402930'
          ? 5
          : 0;
      return {
        ...base,
        star_count: Math.max(0, Math.min(5, resolvedStar)),
      };
    });

    const found = normalizedArtists.find((a) => {
      // 1. Primary: Match by normalized Instagram username
      if (normalizedIgTarget && a.instagram_username === normalizedIgTarget) {
        return true;
      }
      if (a.raw_instagram && normalizeInstagramUsername(a.raw_instagram) === normalizedIgTarget) {
        return true;
      }
      // 2. Legacy fallback for slug, user ID or record ID
      if (a.slug === cleanLower) return true;
      if (a.user_id && a.user_id.toLowerCase() === cleanLower) return true;
      if (a.id && a.id.toLowerCase() === cleanLower) return true;
      if (slugifyArtist(a.stage_name, a.user_id) === cleanLower) return true;
      if (slugifyArtist(a.name, a.user_id) === cleanLower) return true;
      return false;
    });

    return found || null;
  } catch (err) {
    console.error('[artistWebService] Unexpected error in fetchArtistBySlugOrId:', err);
    return null;
  }
}


